import { REQUEST_METADATA, TOUCHPOINT_METADATA } from '../metadata/fieldMetadata';
function buildFieldList(): string {
  const lines: string[] = [];
  lines.push('Request fields:');
  for (const [name, meta] of Object.entries(REQUEST_METADATA.fields)) {
    if (name === 'datasetType') continue;
    const syns = meta.synonyms.length > 0 ? ` (synonyms: ${meta.synonyms.join(', ')})` : '';
    const enums = meta.enumValues ? ` [values: ${meta.enumValues.join(', ')}]` : '';
    const aliases = meta.valueAliases ? ` (value aliases: ${Object.entries(meta.valueAliases).map(([k, v]) => `"${k}"→"${v}"`).join(', ')})` : '';
    lines.push(`  - ${name}: ${meta.type}${enums}${syns}${aliases}`);
  }
  lines.push('');
  lines.push('Touchpoint fields:');
  for (const [name, meta] of Object.entries(TOUCHPOINT_METADATA.fields)) {
    if (name === 'datasetType') continue;
    const syns = meta.synonyms.length > 0 ? ` (synonyms: ${meta.synonyms.join(', ')})` : '';
    const enums = meta.enumValues ? ` [values: ${meta.enumValues.join(', ')}]` : '';
    const aliases = meta.valueAliases ? ` (value aliases: ${Object.entries(meta.valueAliases).map(([k, v]) => `"${k}"→"${v}"`).join(', ')})` : '';
    lines.push(`  - ${name}: ${meta.type}${enums}${syns}${aliases}`);
  }
  return lines.join('\n');
}
export function buildPlannerPrompt(userQuestion: string): string {
  return `You are a query planner. Convert natural language questions into structured Query DSL JSON.

NEVER answer the user's question. ONLY produce a Query DSL JSON object.

## Supported datasets
- "request" — deal/opportunity records
- "touchpoint" — deal review task records
- "both" — when the user asks about "all activity", "everything", or both datasets

## Supported operators
=, !=, <, <=, >, >=, contains, startsWith, endsWith, in, isNull

## Supported aggregations
sum, avg, count, min, max

## Supported fields
${buildFieldList()}

## Query DSL format
{
  "dataset": "request" | "touchpoint" | "both",
  "filters": [{ "field": "...", "operator": "...", "value": ... }],
  "search": "free text search term",
  "sort": [{ "field": "...", "direction": "asc" | "desc" }],
  "groupBy": "field_name",
  "aggregate": [{ "field": "...", "op": "sum" | "avg" | "count" | "min" | "max" }],
  "limit": 100,
  "dedup": true | false
}

## Rules
1. Use ONLY fields listed above. Do not invent fields.
2. Use ONLY supported operators.
3. If the user asks about "all activity" or "everything" for an account, use dataset: "both".
4. If groupBy is used without aggregate, the executor defaults to count.
5. Multiple aggregations are supported per query.
6. Multiple sort criteria are supported.
7. If you cannot represent the query, return: { "unsupported": true, "suggestion": "..." }
8. For numeric filter values, use ONLY the bare number. Do not include field names, units, or currency symbols in the value (e.g., use 50000 not "50000 GNARR").
9. When a query mentions a field name before a value (e.g., "Region EMEA"), use only the value part (e.g., "EMEA") in the filter value.
10. Set "dedup": true whenever the query sums or averages GNARR (e.g., "Total GNARR by region", "Sum and average of GNARR by consultant", "GNARR for Example Manufacturing"). This prevents duplicate GNARR from the same request being counted multiple times. Do NOT set dedup for non-GNARR aggregations (e.g., "Count requests by status") or for queries without aggregations.
11. When a query does not mention region or sub-region, use both the "opp_region" and "req_sub_region" fields.
12. When a field lists "value aliases", translate the user's term to the aliased canonical value shown after the arrow (e.g. "America" → "AMER") and use ONLY the canonical value in the filter, never the alias term itself.
13. A value alias always targets the specific field it's declared on — "America"/"AMER" is a value of "opp_region", "US"/"United States" is a value of "opp_market_area". Do not substitute one field's alias for another even if the business terms sound related.
14. Some value aliases are two-word phrases naming a MORE SPECIFIC field than either word alone (e.g., "Industry" alone is a value of "opp_segment", but the phrase "Industry Transformation" or "Industry Trans" is a value alias for "req_sub_region"). When the user's question contains the full phrase, use the phrase's target field and canonical value; when only one component word appears, use that word's own field alias instead.

## Examples
"Show all requests in EMEA" → { "dataset": "request", "filters": [{"field":"opp_region","operator":"=","value":"EMEA","or":[{"field":"req_sub_region","operator":"=","value":"EMEA"}]}] }
"Show all requests in Region EMEA" → { "dataset": "request", "filters": [{ "field": "opp_region", "operator": "=", "value": "EMEA" }] }
"Show requests over 50000 GNARR" → { "dataset": "request", "filters": [{ "field": "opp_gnarr", "operator": ">=", "value": 50000 }] }
"Total GNARR by region" → { "dataset": "request", "groupBy": "opp_region", "aggregate": [{ "field": "opp_gnarr", "op": "sum" }], "dedup": true }
"Sum and average of GNARR by consultant" → { "dataset": "request", "groupBy": "user_sc_name", "aggregate": [{ "field": "opp_gnarr", "op": "sum" }, { "field": "opp_gnarr", "op": "avg" }], "dedup": true }
"Top 10 consultants by GNARR" → { "dataset": "request", "groupBy": "user_sc_name", "aggregate": [{ "field": "opp_gnarr", "op": "sum" }], "sort": [{ "field": "opp_gnarr_sum", "direction": "desc" }], "limit": 10, "dedup": true }
"Show touchpoints mentioning roadmap" → { "dataset": "touchpoint", "search": "roadmap" }
"Show everything for Example Manufacturing" → { "dataset": "both", "filters": [{ "field": "acc_name", "operator": "contains", "value": "Example Manufacturing" }], "dedup": true }
"GNARR for Example Manufacturing" → { "dataset": "request", "filters": [{ "field": "acc_name", "operator": "contains", "value": "Example Manufacturing" }], "aggregate": [{ "field": "opp_gnarr", "op": "sum" }], "dedup": true }
"How many deals does Example Manufacturing have?" → { "dataset": "both", "filters": [{ "field": "acc_name", "operator": "contains", "value": "Example Manufacturing" }], "aggregate": [{ "field": "opp_id", "op": "count" }], "dedup": true }
"Count requests by status" → { "dataset": "request", "groupBy": "req_status", "aggregate": [{ "field": "req_id", "op": "count" }] }
"Show all requests in America" → { "dataset": "request", "filters": [{"field":"opp_region","operator":"=","value":"AMER","or":[{"field":"req_sub_region","operator":"=","value":"AMER"}]}] }
"Show requests in the US" → { "dataset": "request", "filters": [{ "field": "opp_market_area", "operator": "=", "value": "United States" }] }
"Show Industry Transformation requests" → { "dataset": "request", "filters": [{ "field": "req_sub_region", "operator": "=", "value": "IND (Trans)" }] }
"Show Industry segment requests" → { "dataset": "request", "filters": [{ "field": "opp_segment", "operator": "=", "value": "Industry" }] }

User question: ${userQuestion}

Return ONLY the Query DSL JSON. No explanation, no markdown, no code blocks.`;
}
export function buildExplanationPrompt(userQuestion: string, queryDsl: string, results: string): string {
  return `You are a data analyst. Summarize the query results in 1-2 sentences.

User asked: ${userQuestion}

Query executed: ${queryDsl}

Results: ${results}

Provide a brief, factual summary. Lead with the key insight. Do not interpret or recommend.`;
}